import { renderIdle } from './views/idle';
import { renderActive } from './views/active';
import { renderClaimForm, renderConfirmation } from './views/form';
import { getLang, setLang, saveProfile, clearProfile, type Lang } from './store';
import {
  fetchActiveDrop, submitClaim, uploadPhoto, subscribeDrop,
  type ApiActiveResponse,
} from './api';
import { tr } from './i18n';

// ============================================================================
// App state
// ============================================================================

interface AppState {
  lang: Lang;
  loading: boolean;
  error: string | null;
  drop: ApiActiveResponse | null;
}

const state: AppState = {
  lang: getLang(),
  loading: true,
  error: null,
  drop: null,
};

let unsubscribeWS: (() => void) | null = null;
let selectedPhoto: { file: File; dataUrl: string } | null = null;

// ============================================================================
// Render
// ============================================================================

function mount(html: string) {
  const app = document.getElementById('app')!;
  const fragment = document.createRange().createContextualFragment(html);
  app.replaceChildren(fragment);
}

function render() {
  const lang = state.lang;
  if (state.loading) {
    mount(renderLoading(lang));
  } else if (state.error) {
    mount(renderError(lang, state.error));
  } else if (state.drop?.active) {
    mount(renderActive(lang, state.drop));
    wireActive();
  } else {
    mount(renderIdle(lang));
    wireStats();
  }
  wireShell();
}

function wireStats() {
  const nums = document.querySelectorAll<HTMLElement>('.sq-stat-n[data-count]');
  if (!nums.length) return;
  const animate = (el: HTMLElement) => {
    const target = parseInt(el.dataset.count || '0', 10);
    const duration = 1100;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = String(Math.round(target * eased));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        animate(e.target as HTMLElement);
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.4 });
  nums.forEach((n) => io.observe(n));
}

function renderLoading(_lang: Lang): string {
  return /* html */ `
    <div class="sq-loading">
      <div class="sq-loading-spinner"></div>
    </div>
  `;
}

function renderError(lang: Lang, msg: string): string {
  const retry = lang === 'ro' ? 'Reîncearcă' : 'Retry';
  return /* html */ `
    <div class="sq-loading">
      <p class="sq-loading-error">${escapeHtml(msg)}</p>
      <button class="sq-cta" id="sq-retry">${retry}</button>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

// ============================================================================
// Wiring
// ============================================================================

function wireShell() {
  document.querySelectorAll<HTMLButtonElement>('.sq-lang button').forEach((btn) => {
    btn.addEventListener('click', () => {
      const lang = btn.dataset.lang as Lang;
      if (!lang || lang === state.lang) return;
      state.lang = lang;
      setLang(lang);
      render();
    });
  });
  document.getElementById('sq-retry')?.addEventListener('click', () => {
    void refresh();
  });
}

function wireActive() {
  const cta = document.getElementById('sq-claim-cta') as HTMLButtonElement | null;
  if (cta && !cta.disabled) {
    cta.addEventListener('click', () => openClaimForm());
  }
}

// ============================================================================
// Data
// ============================================================================

async function refresh() {
  try {
    state.loading = state.drop === null;
    state.error = null;
    render();
    const data = await fetchActiveDrop();
    state.drop = data;
    state.loading = false;
    state.error = null;
    render();
    setupSubscription();
  } catch (e) {
    state.loading = false;
    state.error = e instanceof Error ? e.message : String(e);
    render();
  }
}

function setupSubscription() {
  if (unsubscribeWS) return;
  const dropId = state.drop?.active?.id;
  if (!dropId) return;
  unsubscribeWS = subscribeDrop(dropId, () => {
    // Any event = re-fetch full state. Cheap and accurate.
    void refetchSilent();
  });
}

async function refetchSilent() {
  try {
    const data = await fetchActiveDrop();
    state.drop = data;
    // Don't re-render if modal is open (would close it). Just update underlying state.
    if (!document.querySelector('.sq-modal')) {
      render();
    }
  } catch { /* ignore — next event or manual refresh */ }
}

// ============================================================================
// Modal + form
// ============================================================================

function ensureModalLayer(): HTMLElement {
  let layer = document.getElementById('sq-modal-layer');
  if (!layer) {
    layer = document.createElement('div');
    layer.id = 'sq-modal-layer';
    document.body.appendChild(layer);
  }
  return layer;
}

function closeModal() {
  const layer = document.getElementById('sq-modal-layer');
  if (layer) layer.replaceChildren();
  document.body.classList.remove('sq-modal-open');
  selectedPhoto = null;
}

function openClaimForm() {
  if (!state.drop?.active || state.drop.active.status !== 'active') {
    void refresh();
    return;
  }
  const layer = ensureModalLayer();
  const fragment = document.createRange().createContextualFragment(renderClaimForm(state.lang));
  layer.replaceChildren(fragment);
  document.body.classList.add('sq-modal-open');
  wireForm();
}

function wireForm() {
  const lang = state.lang;
  const L = tr(lang);

  document.getElementById('sq-modal-close')?.addEventListener('click', closeModal);
  document.querySelector('.sq-modal')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeModal();
  });

  document.getElementById('sq-reset-profile')?.addEventListener('click', () => {
    clearProfile();
    closeModal();
    openClaimForm();
  });

  const photoInput = document.getElementById('f-dog-photo') as HTMLInputElement;
  const placeholder = document.getElementById('sq-photo-placeholder')!;
  const preview = document.getElementById('sq-photo-preview')!;
  const img = document.getElementById('sq-photo-img') as HTMLImageElement;
  const changeBtn = document.getElementById('sq-photo-change');

  photoInput?.addEventListener('change', async () => {
    const file = photoInput.files?.[0];
    if (!file) return;
    const dataUrl = await readAsDataUrl(file);
    selectedPhoto = { file, dataUrl };
    img.src = dataUrl;
    placeholder.hidden = true;
    preview.hidden = false;
  });

  changeBtn?.addEventListener('click', () => {
    photoInput.value = '';
    selectedPhoto = null;
    placeholder.hidden = false;
    preview.hidden = true;
  });

  const form = document.getElementById('sq-form') as HTMLFormElement;
  const submitBtn = document.getElementById('sq-form-submit') as HTMLButtonElement;
  const errorBox = document.getElementById('sq-form-error') as HTMLDivElement;

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorBox.hidden = true;
    errorBox.textContent = '';

    const dropId = state.drop?.active?.id;
    if (!dropId || state.drop?.active?.status !== 'active') {
      closeModal();
      await refresh();
      return;
    }

    if (!selectedPhoto) {
      errorBox.textContent = L.err_photo;
      errorBox.hidden = false;
      return;
    }
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const fd = new FormData(form);
    if (fd.get('consent_terms') !== 'on' || fd.get('consent_content_usage') !== 'on') {
      errorBox.textContent = L.err_consent;
      errorBox.hidden = false;
      return;
    }

    const ownerData = {
      dog_name: String(fd.get('dog_name') || '').trim(),
      dog_breed: String(fd.get('dog_breed') || '').trim(),
      dog_age_range: String(fd.get('dog_age_range') || ''),
      dog_size: String(fd.get('dog_size') || ''),
      dog_gender: String(fd.get('dog_gender') || ''),
      dog_city: String(fd.get('dog_city') || '').trim(),
      dog_superpower: String(fd.get('dog_superpower') || '').trim(),
      dog_hobby: String(fd.get('dog_hobby') || '').trim(),
      owner_name: String(fd.get('owner_name') || '').trim(),
      instagram_handle: normalizeHandle(String(fd.get('instagram_handle') || '')),
      email: String(fd.get('email') || '').trim().toLowerCase(),
      phone: String(fd.get('phone') || '').trim(),
    };

    submitBtn.disabled = true;
    submitBtn.textContent = L.btn_submit_loading;

    try {
      // 1. Upload photo
      const photoRes = await uploadPhoto(selectedPhoto.file);

      // 2. Submit claim
      const claimRes = await submitClaim(dropId, {
        ...ownerData,
        dog_photo_key: photoRes.key,
        consent_terms: true,
        consent_content_usage: true,
        consent_newsletter: fd.get('consent_newsletter') === 'on',
        lang,
      });

      if ('ok' in claimRes && claimRes.ok === false) {
        const err = claimRes.error;
        if (err === 'already_claimed') {
          errorBox.textContent = L.err_dup;
        } else if (err === 'too_many_no_shows') {
          errorBox.textContent = L.err_strikes;
        } else if (err === 'drop_not_active') {
          errorBox.textContent = L.err_full;
          setTimeout(() => { closeModal(); void refresh(); }, 1500);
        } else if (err === 'drop_full') {
          errorBox.textContent = L.err_full;
          setTimeout(() => { closeModal(); void refresh(); }, 1500);
        } else {
          errorBox.textContent = `Error: ${err}`;
        }
        errorBox.hidden = false;
        submitBtn.disabled = false;
        submitBtn.textContent = L.btn_submit;
        return;
      }

      saveProfile(ownerData);

      const ok = claimRes as { ok: true; position: number; on_waitlist: boolean; claim_id: string };
      showConfirmation(ownerData.dog_name, ok.position, ok.on_waitlist, {
        claimId: ok.claim_id,
        dropId,
        isNewsletter: fd.get('consent_newsletter') === 'on',
      });
      // refresh state in background
      void refetchSilent();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errorBox.textContent = msg;
      errorBox.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = L.btn_submit;
    }
  });
}

function showConfirmation(
  dogName: string,
  position: number,
  onWaitlist: boolean,
  push: { claimId: string; dropId: string; isNewsletter: boolean },
) {
  const layer = ensureModalLayer();
  const fragment = document.createRange().createContextualFragment(
    renderConfirmation(state.lang, dogName, position, onWaitlist),
  );
  layer.replaceChildren(fragment);
  document.getElementById('sq-confirm-close')?.addEventListener('click', () => {
    closeModal();
    void refresh();
  });
  wirePushOptIn(push);
}

async function wirePushOptIn(push: { claimId: string; dropId: string; isNewsletter: boolean }) {
  const { pushSupported, pushPermission, subscribePush } = await import('./push');
  if (!pushSupported() || pushPermission() === 'denied') return;

  const card = document.querySelector<HTMLElement>('.sq-confirm');
  if (!card) return;

  const wrap = document.createElement('div');
  wrap.className = 'sq-push-optin';
  const txt = document.createElement('div');
  txt.className = 'sq-push-optin-text';
  txt.textContent = state.lang === 'ro'
    ? 'Notificare push când urci sau când e ziua quest-ului.'
    : 'Push notification when you move up or on quest day.';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sq-push-btn';
  btn.textContent = state.lang === 'ro' ? 'Activează' : 'Enable';
  const status = document.createElement('div');
  status.className = 'sq-push-status';
  status.hidden = true;
  wrap.append(txt, btn, status);
  // Insert above the yellow "validate via email" warning so the most urgent message stays closest to the CTA.
  // Fallback to above the close button if the warning isn't rendered (e.g. waitlist confirmation).
  const anchor = card.querySelector('.sq-confirm-validate') ?? card.querySelector('#sq-confirm-close');
  if (anchor) card.insertBefore(wrap, anchor); else card.appendChild(wrap);

  btn.addEventListener('click', async () => {
    btn.disabled = true;
    btn.textContent = '…';
    const r = await subscribePush('/api', {
      ownerType: 'claim',
      claimId: push.claimId,
      dropId: push.dropId,
      lang: state.lang,
      isNewsletter: push.isNewsletter,
    });
    if (r.ok) {
      wrap.classList.add('is-ok');
      btn.hidden = true;
      status.hidden = false;
      status.textContent = state.lang === 'ro' ? '✓ Notificări active' : '✓ Notifications on';
    } else {
      btn.disabled = false;
      btn.textContent = state.lang === 'ro' ? 'Activează notificări' : 'Enable notifications';
      status.hidden = false;
      status.textContent = state.lang === 'ro'
        ? 'Permisiunea a fost respinsă. Verifică setările browserului.'
        : 'Permission denied. Check your browser settings.';
    }
  });
}

function normalizeHandle(h: string): string {
  const x = h.trim();
  if (!x) return '';
  return x.startsWith('@') ? x : '@' + x;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// ============================================================================
// Boot — route by pathname (admin SPA at /admin)
// ============================================================================

if (location.pathname.startsWith('/admin')) {
  void import('./admin/app').then((m) => m.bootAdmin());
} else {
  void refresh();
  void maybeShowExplainer();
}

async function maybeShowExplainer() {
  try {
    const forceShow = new URLSearchParams(location.search).has('explainer');
    if (forceShow) {
      localStorage.removeItem('sq-explainer-dismissed');
    }
    const r = await fetch('/api/config');
    if (!r.ok) return;
    const cfg = await r.json() as { show_explainer?: boolean };
    if (!cfg.show_explainer && !forceShow) return;
    const { shouldShowExplainer, showExplainer } = await import('./explainer');
    if (!shouldShowExplainer() && !forceShow) return;
    setTimeout(() => showExplainer(state.lang, () => { /* nothing */ }), 600);
  } catch { /* ignore */ }
}

// ============================================================================
// Service worker registration + install prompt
// ============================================================================

if ('serviceWorker' in navigator && location.protocol === 'https:') {
  window.addEventListener('load', () => {
    void registerServiceWorkerWithUpdates();
  });
}

function isPwaStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as any).standalone === true;
}

async function registerServiceWorkerWithUpdates() {
  let reg: ServiceWorkerRegistration;
  try {
    reg = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
  } catch { return; }

  // Force a fresh update check on every page load
  try { await reg.update(); } catch { /* ignore */ }

  const handleWaiting = (waiting: ServiceWorker) => {
    // Silent update everywhere. Next navigation/refresh picks up the new bundle naturally.
    waiting.postMessage({ type: 'SKIP_WAITING' });
  };

  if (reg.waiting && navigator.serviceWorker.controller) {
    handleWaiting(reg.waiting);
  }

  reg.addEventListener('updatefound', () => {
    const nw = reg.installing;
    if (!nw) return;
    nw.addEventListener('statechange', () => {
      if (nw.state === 'installed' && navigator.serviceWorker.controller) {
        handleWaiting(nw);
      }
    });
  });

  // No auto-reload — let the page keep running. New JS loads at next navigation / user refresh.
}

function showUpdateBanner(waitingWorker: ServiceWorker) {
  if (document.getElementById('sq-update-banner')) return;
  const bar = document.createElement('div');
  bar.id = 'sq-update-banner';
  bar.className = 'sq-update-banner';
  const text = document.createElement('div');
  text.className = 'sq-update-banner-text';
  text.textContent = 'Versiune nouă disponibilă.';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sq-update-banner-btn';
  btn.textContent = 'Reîncarcă';
  bar.append(text, btn);
  document.body.appendChild(bar);
  btn.addEventListener('click', () => {
    btn.disabled = true;
    btn.textContent = '…';
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  });
}

let deferredInstallPrompt: any = null;

window.addEventListener('beforeinstallprompt', (e: any) => {
  e.preventDefault();
  deferredInstallPrompt = e;
  showInstallButton();
});

window.addEventListener('appinstalled', () => {
  deferredInstallPrompt = null;
  hideInstallButton();
});

// iOS Safari has no beforeinstallprompt. Show a manual install hint instead.
function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
}
function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as any).standalone === true;
}
function isIosSafari(): boolean {
  if (!isIos()) return false;
  const ua = navigator.userAgent;
  // Exclude Chrome/Firefox/etc on iOS (they can't install PWA the same way)
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

window.addEventListener('load', () => {
  if (isIosSafari() && !isStandalone() && !localStorage.getItem('sq-ios-install-dismissed')) {
    setTimeout(showIosInstallHint, 1200);
  }
});

function showIosInstallHint() {
  if (document.getElementById('sq-ios-hint')) return;
  const bar = document.createElement('div');
  bar.id = 'sq-ios-hint';
  bar.className = 'sq-ios-hint';
  const text = document.createElement('div');
  text.className = 'sq-ios-hint-text';
  const title = document.createElement('strong');
  title.textContent = 'Instalează Sniff Quest';
  const sub = document.createElement('span');
  sub.textContent = ' apasă Share apoi Add to Home Screen.';
  const shareIcon = document.createElement('span');
  shareIcon.className = 'sq-ios-share';
  shareIcon.setAttribute('aria-hidden', 'true');
  shareIcon.textContent = '⇪';
  text.append(title, document.createElement('br'), sub);
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'sq-ios-close';
  close.setAttribute('aria-label', 'Închide');
  close.textContent = '×';
  bar.append(shareIcon, text, close);
  document.body.appendChild(bar);
  close.addEventListener('click', () => {
    localStorage.setItem('sq-ios-install-dismissed', '1');
    bar.remove();
  });
}

function showInstallButton() {
  if (document.getElementById('sq-install-btn')) return;
  const btn = document.createElement('button');
  btn.id = 'sq-install-btn';
  btn.className = 'sq-install-btn';
  btn.type = 'button';
  const label = document.createElement('span');
  label.textContent = 'Instalează';
  const arrow = document.createElement('span');
  arrow.className = 'sq-install-arrow';
  arrow.textContent = '→';
  btn.append(label, arrow);
  btn.addEventListener('click', async () => {
    if (!deferredInstallPrompt) return;
    deferredInstallPrompt.prompt();
    try { await deferredInstallPrompt.userChoice; } catch { /* ignore */ }
    deferredInstallPrompt = null;
    hideInstallButton();
  });
  document.body.appendChild(btn);
}

function hideInstallButton() {
  document.getElementById('sq-install-btn')?.remove();
}
